import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import BackdropComponent from "../../Spinner/Spinner";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import LastUpdateTooltip from "../Helpers/LastUpdateTooltip";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  getOverrideMilestones,
  savemilestonesData,
  getAllMilestonesWithStatuses,
} from "services/userServices";
import { useNavigate } from "react-router-dom";
import classes from "./MilestonesV2.module.css";
import SnackBar from "components/SnackBar/SnackBar";
import { useTranslation } from "react-i18next";
import { bulkEditStoreAction } from "../../../Store/BulkEdit";
import PowerSharpIcon from "@mui/icons-material/PowerSharp";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import applicationUsageLog from "../../../utils/applicationUsagesLog";
import { Autocomplete, Typography, Box, Button, Chip, styled } from "@mui/material";

import {
  LimitChip,
  StyledChip,
} from "components/IdmCredentialTracking/NoRosterIdCreateUpdateIdmRecord";

// Styled Buttons matching ASTT theme
const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  padding: "8px 20px",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  padding: "8px 20px",
  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

const traineeMilestoneStatus = (data, milestoneId, classTraineeId) => {
  const details = data.find(
    (item) =>
      item?.ClassTraineeId === classTraineeId &&
      item?.MilestoneID === milestoneId,
  );

  return details === undefined ? 0 : details;
};

function omit(obj, ...props) {
  const result = { ...obj };
  props.forEach(function (prop) {
    delete result[prop];
  });
  return result;
}

const initialMilestoneStatus = [
  {
    milestoneID: 0,
    milestone: null,
    milestoneTypeID: 0,
    milestoneType: null,
    dataTypeId: 4,
    dataType: "MultiSelect",
    isActive: true,
  },
];

const traineeDetails = (data, classTraineeId) => {
  const details = data.find((item) => item?.ClassTraineeId === classTraineeId);
  return omit(
    details,
    "Milestone",
    "MilestoneID",
    "MilestoneStatusId",
    "Status",
  );
};

// Helper function to get milestone value based on data type
const getMilestoneValue = (milestoneStatus, milestone) => {
  if (!milestoneStatus) {
    return milestone.dataType.toLowerCase() === "list" ? 0 : "";
  }

  const isListOrMultiSelect =
    milestone.dataType.toLowerCase() === "list" ||
    milestone.dataType.toLowerCase() === "multiselect";

  return isListOrMultiSelect
    ? milestoneStatus.MilestoneStatusId
    : milestoneStatus.Status;
};

const prepareMilestonesDetails = (data, milestones, classTraineeId) => {
  return milestones.map((milestone) => {
    const milestoneStatus = traineeMilestoneStatus(
      data,
      milestone?.milestoneID,
      classTraineeId,
    );

    const milestoneValue = getMilestoneValue(milestoneStatus, milestone);

    return {
      [milestone.milestone]: milestoneValue,
    };
  });
};

const createData = (data, milestones) => {
  const distinctTrainees = [
    ...new Set(data.map((item) => item?.ClassTraineeId)),
  ];

  return distinctTrainees.map((item) => {
    const milestoneDetails = Object.assign(
      {},
      ...prepareMilestonesDetails(data, milestones, item),
    );
    return {
      ...traineeDetails(data, item),
      ...milestoneDetails,
    };
  });
};

export const mergeByMilestoneId = (array1, array2) =>
  array1.map((itm) => ({
    ...array2.find((item) => item.milestoneID === itm.milestoneID && item),
    ...itm,
  }));

// Extract header cell components
const NameHeaderCell = ({ sorting, t, order, totalActiveCount, classes }) => (
  <TableCell
    className={classes.tableHeaderCell}
    align="center"
    sx={{
      minWidth: "400px !important",
      maxWidth: 400,
      width: 400,
      position: "sticky",
      left: 0,
      top: "0 !important",
      zIndex: "1100 !important",
      boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
      cursor: "pointer",
      backgroundColor: "#e5e4e2 !important",
    }}
    onClick={() => sorting("Name")}
  >
    <Typography
      sx={{
        fontFamily: "Urbanist",
        fontSize: "14px",
        fontWeight: "bold",
      }}
    >
      {t("Name/OracleId")}{" "}
      <ArrowUpwardIcon
        sx={{
          fontSize: 16,
          color: order === "ASC" ? "#3299FF" : "#3F5765",
          verticalAlign: "middle",
        }}
      />
      <ArrowDownwardIcon
        sx={{
          fontSize: 16,
          color: order === "DSC" ? "#3299FF" : "#3F5765",
          verticalAlign: "middle",
        }}
      />
    </Typography>
    <Typography
      className={classes.pinkColor}
      sx={{ fontSize: "12px", mt: 0.5 }}
    >
      Total: {totalActiveCount.total} | Active: {totalActiveCount.active}
    </Typography>
  </TableCell>
);

// List Header Input Component
const ListHeaderInput = ({
  item,
  headDropDownStatuses,
  getStatuses,
  handleAllDropDown,
  classes,
}) => (
  <FormControl className={classes.Select} size="small">
    <Select
      labelId={`head-select-label-${item?.milestoneID}`}
      id={`head-select-${item?.milestoneID}`}
      onChange={(e) => handleAllDropDown(e.target.value, item?.milestone)}
      value={headDropDownStatuses[item?.milestone] ?? 0}
      displayEmpty
      sx={{
        fontFamily: "Urbanist",
        fontSize: "12px",
        bgcolor: "#fff",
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            "& .MuiMenuItem-root": {
              fontFamily: "Urbanist",
            },
            "& .MuiMenuItem-root.Mui-selected": {
              backgroundColor: "rgba(101, 128, 187, 1)",
            },
            "& .MuiMenuItem-root:hover": {
              backgroundColor: "rgba(209, 217, 234, 1)",
            },
          },
        },
      }}
    >
      <MenuItem value=""></MenuItem>
      {getStatuses(item?.milestoneID).map((statusItem) => (
        <MenuItem
          key={statusItem.milestoneStatusId}
          value={statusItem.milestoneStatusId}
        >
          <b>{statusItem.status}</b>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

// Memo Header Input Component
const MemoHeaderInput = ({
  item,
  globalMemoText,
  headDropDownStatuses,
  handleAllMemoTextField,
  setGlobalMemoText,
}) => {
  const currentLength = headDropDownStatuses[item?.milestone]?.length || 0;
  const isMaxLength = currentLength >= 2000;

  return (
    <TextField
      variant="outlined"
      multiline
      rows={2.5}
      id="outlined-basic"
      name={item.milestone}
      value={globalMemoText[item?.milestone]}
      placeholder="Max 2000 Characters Allowed"
      helperText={`${currentLength}/2000`}
      inputProps={{ maxLength: 2000 }}
      onChange={(event) => {
        setGlobalMemoText(event.target.value);
        handleAllMemoTextField(event.target.value, item?.milestone);
      }}
      sx={{
        width: 300,
        backgroundColor: "#fff",
        fontFamily: "Urbanist",
      }}
      FormHelperTextProps={{
        sx: {
          color: isMaxLength ? "red" : undefined,
          position: "absolute",
          bottom: 8,
          right: 8,
        },
      }}
    />
  );
};

// MultiSelect Header Input Component
const MultiSelectHeaderInput = ({
  item,
  headDropDownStatuses,
  getStatuses,
  handleMultiSelectAllChange,
  getMultiSelectSelectedValue,
}) => (
  <Autocomplete
    multiple
    disablePortal
    options={getStatuses(item.milestoneID)}
    getOptionLabel={(option) => option.status}
    value={getMultiSelectSelectedValue(
      headDropDownStatuses[item?.milestone] ?? "",
      item.milestoneID,
    )}
    onChange={(event, value) =>
      handleMultiSelectAllChange(event, value, item?.milestone)
    }
    renderInput={(params) => (
      <TextField
        {...params}
        placeholder="Select Status"
        sx={{
          backgroundColor: "#fff",
          fontFamily: "Urbanist",
        }}
      />
    )}
    renderTags={(value, getTagProps) => {
      const limitTags = 1;
      const more = value.length - limitTags;
      const hiddenLabels = value
        .slice(limitTags)
        .map((opt) => opt.status)
        .join(", ");

      return (
        <>
          {value.slice(0, limitTags).map((option, index) => (
            <StyledChip
              key={option.status}
              label={option.status}
              size="small"
              {...getTagProps({ index })}
            />
          ))}
          {more > 0 && (
            <Tooltip title={hiddenLabels} arrow placement="top">
              <LimitChip label={`+${more}`} size="small" />
            </Tooltip>
          )}
        </>
      );
    }}
  />
);

// Date Header Input Component
const DateHeaderInput = ({ item, globalDate, handleAllDateField }) => {
  const renderDateInput = (params) => {
    const inputValue = params.inputProps?.value || "";
    const isComplete = inputValue.length === 10;
    const isValidDate = dayjs(inputValue, "MM/DD/YYYY", true).isValid();

    return (
      <TextField
        {...params}
        size="small"
        sx={{
          width: 150,
          backgroundColor: "#fff",
          fontFamily: "Urbanist",
        }}
        error={isComplete && !isValidDate}
        helperText={isComplete && !isValidDate ? "Invalid date format" : ""}
      />
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        inputFormat="MM/DD/YYYY"
        value={
          globalDate[item?.milestone]
            ? dayjs(globalDate[item?.milestone], "MM/DD/YYYY")
            : null
        }
        onChange={(newValue) => {
          const formatted = newValue?.isValid?.()
            ? newValue.format("MM/DD/YYYY")
            : "";
          handleAllDateField(formatted, item?.milestone);
        }}
        renderInput={renderDateInput}
      />
    </LocalizationProvider>
  );
};

// Text Header Input Component
const TextHeaderInput = ({
  item,
  globalText,
  handleAllTextField,
  setGlobalText,
}) => {
  const isNumber = item?.dataType === "Number";

  return (
    <TextField
      type={isNumber ? "number" : "text"}
      id={isNumber ? "standard-number" : "outlined-basic"}
      name={item.milestone}
      value={globalText[item?.milestone]}
      placeholder={
        isNumber ? "Only Numerics Allowed" : "Max 30 Characters Allowed"
      }
      inputProps={{ maxLength: isNumber ? 10 : 30 }}
      onChange={(event) => {
        setGlobalText(event.target.value);
        handleAllTextField(event.target.value, item?.milestone);
      }}
      sx={{
        width: 140,
        backgroundColor: "#fff",
        fontFamily: "Urbanist",
      }}
      size="small"
      fullWidth
    />
  );
};

// Milestone Header Cell Component
const MilestoneHeaderCell = ({
  item,
  headDropDownStatuses,
  globalMemoText,
  globalDate,
  globalText,
  getStatuses,
  handleAllDropDown,
  handleAllMemoTextField,
  handleMultiSelectAllChange,
  handleAllDateField,
  handleAllTextField,
  setGlobalMemoText,
  setGlobalText,
  getMultiSelectSelectedValue,
  classes,
}) => {
  const dataType = item?.dataType?.toLowerCase();

  const renderInput = () => {
    switch (dataType) {
      case "list":
        return (
          <ListHeaderInput
            item={item}
            headDropDownStatuses={headDropDownStatuses}
            getStatuses={getStatuses}
            handleAllDropDown={handleAllDropDown}
            classes={classes}
          />
        );
      case "memo":
        return (
          <MemoHeaderInput
            item={item}
            globalMemoText={globalMemoText}
            headDropDownStatuses={headDropDownStatuses}
            handleAllMemoTextField={handleAllMemoTextField}
            setGlobalMemoText={setGlobalMemoText}
          />
        );
      case "multiselect":
        return (
          <MultiSelectHeaderInput
            item={item}
            headDropDownStatuses={headDropDownStatuses}
            getStatuses={getStatuses}
            handleMultiSelectAllChange={handleMultiSelectAllChange}
            getMultiSelectSelectedValue={getMultiSelectSelectedValue}
          />
        );
      case "date":
        return (
          <DateHeaderInput
            item={item}
            globalDate={globalDate}
            handleAllDateField={handleAllDateField}
          />
        );
      default:
        return (
          <TextHeaderInput
            item={item}
            globalText={globalText}
            handleAllTextField={handleAllTextField}
            setGlobalText={setGlobalText}
          />
        );
    }
  };

  return (
    <TableCell
      key={item?.milestoneID}
      className={classes.tableHeaderCell}
      align="center"
      sx={{
        backgroundColor: "#e5e4e2 !important",
      }}
    >
      <Typography
        sx={{
          fontFamily: "Urbanist",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        {item?.milestone}
      </Typography>
      <br />
      {renderInput()}
    </TableCell>
  );
};

// Row Components
const ListRowInput = ({ row, item, getStatuses, handleChange }) => (
  <FormControl sx={{ width: 140, backgroundColor: "#fff" }} size="small">
    <InputLabel id={`label-${row.ClassTraineeId}-${item.milestoneID}`} />
    <Select
      id={`select-${row.ClassTraineeId}-${item.milestoneID}`}
      name={item.milestone}
      value={row[item.milestone] ?? 0}
      onChange={(event) =>
        handleChange(event.target.value, [item?.milestone], row)
      }
      sx={{
        fontFamily: "Urbanist",
        fontSize: "12px",
      }}
    >
      <MenuItem value={0}> </MenuItem>
      {getStatuses(item.milestoneID).map((status) => (
        <MenuItem
          key={status.milestoneStatusId}
          value={status.milestoneStatusId}
        >
          <b>{status.status}</b>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const DateRowInput = ({ row, item, handleRowDateField }) => {
  // A legacy value from a prior Data Type (e.g. Text/Memo) fails this parse;
  // show it as a read-only chip with a Replace action instead of blanking it.
  const rawValue = row[item.milestone];
  const parsedDate = rawValue ? dayjs(rawValue, "MM-DD-YYYY", true) : null;
  const isLegacyUnparseable = !!rawValue && !parsedDate?.isValid();
  const [replacing, setReplacing] = useState(false);

  const renderDateInput = (params) => {
    const inputValue = params.inputProps?.value || "";
    const isComplete = inputValue.length === 10;
    const isValidDate = dayjs(inputValue, "MM/DD/YYYY", true).isValid();

    return (
      <TextField
        {...params}
        size="small"
        sx={{
          width: 150,
          backgroundColor: "#fff",
          fontFamily: "Urbanist",
        }}
        error={isComplete && !isValidDate}
        helperText={isComplete && !isValidDate ? "Invalid date format" : ""}
      />
    );
  };

  if (isLegacyUnparseable && !replacing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Chip
          label={rawValue}
          size="small"
          variant="outlined"
          title={`Recorded before this Milestone's Data Type was changed: "${rawValue}"`}
          sx={{ maxWidth: 130, fontFamily: "Urbanist" }}
        />
        <Button size="small" onClick={() => setReplacing(true)}>
          Replace
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        inputFormat="MM/DD/YYYY"
        id={`input-${row.ClassTraineeId}-${item.milestoneID}`}
        name={item.milestone}
        value={parsedDate && parsedDate.isValid() ? parsedDate : null}
        onChange={(newValue) =>
          handleRowDateField(newValue, item.milestone, row)
        }
        renderInput={renderDateInput}
      />
    </LocalizationProvider>
  );
};

const TextRowInput = ({ row, item, handleChange }) => {
  const isNumber = item.dataType === "Number";
  const rawValue = row[item.milestone];
  // Number-only: a native number input can't display a non-numeric legacy string.
  const isLegacyUnparseable =
    isNumber && rawValue != null && rawValue !== "" && isNaN(Number(rawValue));
  const [replacing, setReplacing] = useState(false);

  if (isLegacyUnparseable && !replacing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Chip
          label={rawValue}
          size="small"
          variant="outlined"
          title={`Recorded before this Milestone's Data Type was changed: "${rawValue}"`}
          sx={{ maxWidth: 100, fontFamily: "Urbanist" }}
        />
        <Button size="small" onClick={() => setReplacing(true)}>
          Replace
        </Button>
      </Box>
    );
  }

  return (
    <TextField
      type={isNumber ? "number" : "text"}
      id={`input-${row.ClassTraineeId}-${item.milestoneID}`}
      name={item.milestone}
      value={row[item.milestone]}
      placeholder={
        isNumber ? "Only Numerics Allowed" : "Max 30 Characters Allowed"
      }
      inputProps={{ maxLength: isNumber ? 10 : 30 }}
      onChange={(event) =>
        handleChange(event.target.value, [item?.milestone], row)
      }
      sx={{
        width: 140,
        backgroundColor: "#fff",
        fontFamily: "Urbanist",
      }}
      size="small"
      fullWidth
    />
  );
};

const MemoRowInput = ({ row, item, handleMemoChange }) => {
  const currentLength = row[item.milestone]?.length || 0;
  const isMaxLength = currentLength >= 2000;

  return (
    <TextField
      variant="outlined"
      multiline
      rows={2.5}
      id={`input-${row.ClassTraineeId}-${item.milestoneID}`}
      name={item.milestone}
      value={row[item.milestone]}
      placeholder="Max 2000 Characters Allowed"
      helperText={`${currentLength}/2000`}
      inputProps={{ maxLength: 2000 }}
      onChange={(event) =>
        handleMemoChange(event.target.value, [item?.milestone], row)
      }
      sx={{
        width: 300,
        backgroundColor: "#fff",
        fontFamily: "Urbanist",
      }}
      FormHelperTextProps={{
        sx: {
          color: isMaxLength ? "red" : undefined,
          position: "absolute",
          bottom: 8,
          right: 8,
        },
      }}
    />
  );
};

const MultiSelectRowInput = ({
  row,
  item,
  getStatuses,
  handleMultiSelectChange,
  getMultiSelectSelectedValue,
}) => (
  <Autocomplete
    multiple
    disablePortal
    options={getStatuses(item.milestoneID)}
    getOptionLabel={(option) => option.status}
    value={getMultiSelectSelectedValue(row[item.milestone], item.milestoneID)}
    onChange={(event, value) =>
      handleMultiSelectChange(event, value, [item?.milestone], row)
    }
    renderInput={(params) => (
      <TextField
        {...params}
        placeholder="Select Status"
        sx={{
          backgroundColor: "#fff",
          fontFamily: "Urbanist",
        }}
      />
    )}
    renderTags={(value, getTagProps) => {
      const limitTags = 1;
      const more = value.length - limitTags;
      const hiddenLabels = value
        .slice(limitTags)
        .map((opt) => opt.status)
        .join(", ");

      return (
        <>
          {value.slice(0, limitTags).map((option, index) => (
            <StyledChip
              key={option.status}
              label={option.status}
              size="small"
              {...getTagProps({ index })}
            />
          ))}
          {more > 0 && (
            <Tooltip title={hiddenLabels} arrow placement="top">
              <LimitChip label={`+${more}`} size="small" />
            </Tooltip>
          )}
        </>
      );
    }}
  />
);

// Milestone Data Cell Component
const MilestoneDataCell = ({
  row,
  item,
  getStatuses,
  handleChange,
  handleRowDateField,
  handleMemoChange,
  handleMultiSelectChange,
  getMultiSelectSelectedValue,
}) => {
  const dataType = item?.dataType?.toLowerCase();

  const renderInput = () => {
    switch (dataType) {
      case "list":
        return (
          <ListRowInput
            row={row}
            item={item}
            getStatuses={getStatuses}
            handleChange={handleChange}
          />
        );
      case "date":
        return (
          <DateRowInput
            row={row}
            item={item}
            handleRowDateField={handleRowDateField}
          />
        );
      case "number":
      case "text":
        return (
          <TextRowInput row={row} item={item} handleChange={handleChange} />
        );
      case "memo":
        return (
          <MemoRowInput
            row={row}
            item={item}
            handleMemoChange={handleMemoChange}
          />
        );
      case "multiselect":
        return (
          <MultiSelectRowInput
            row={row}
            item={item}
            getStatuses={getStatuses}
            handleMultiSelectChange={handleMultiSelectChange}
            getMultiSelectSelectedValue={getMultiSelectSelectedValue}
          />
        );
      default:
        return null;
    }
  };

  return (
    <TableCell
      key={`${row?.OracleId}-${item?.milestoneID}`}
      align="center"
      sx={{ padding: 0.75 }}
    >
      {renderInput()}
    </TableCell>
  );
};

// Trainee Info Cell Component
const TraineeInfoCell = ({ row, renderTextCell, handleTextInput, classes }) => (
  <TableCell
    component="th"
    scope="row"
    align="center"
    sx={{
      position: "sticky",
      left: 0,
      backgroundColor: "#fff",
      zIndex: 1000,
      boxShadow: "2px 0 5px -2px rgba(0,0,0,0.15)",
      padding: "12px 8px",
      minWidth: 400,
      maxWidth: 400,
      width: 400,
    }}
  >
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="center"
      spacing={0}
      columns={1}
    >
      {renderTextCell(
        row.OracleId,
        row.Name,
        row.TraineeStatus,
        row.TraineeStatus !== "Active",
      )}
      {row.TTECRegistrationStatus ? (
        <Grid item xs={0.1}>
          <Tooltip title="TTEConnect: Registered">
            <PowerSharpIcon sx={{ color: "#69DE00", cursor: "pointer" }} />
          </Tooltip>
        </Grid>
      ) : (
        <Grid item xs={0.1}></Grid>
      )}
    </Grid>

    <Box sx={{ width: "100%", mt: 2 }}>
      <LastUpdateTooltip
        AgentCommentsModifiedDate={row.AgentCommentsModifiedDate}
        AgentCommentsModifiedBy={row.AgentCommentsModifiedBy}
      >
        <textarea
          className={classes.inputContainer}
          value={row.AgentComments || ""}
          name="AgentComments"
          maxLength={4000}
          placeholder="Max 4000 characters allowed"
          onChange={(event) => handleTextInput(event, row)}
        />
      </LastUpdateTooltip>
    </Box>
  </TableCell>
);

const MilestonesV2 = (props) => {
  const { t } = useTranslation(["milestones"]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [milestonesList, setMilestonesList] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [globalText, setGlobalText] = useState("");
  const [globalMemoText, setGlobalMemoText] = useState("");
  const [globalDate, setGlobalDate] = useState("");
  const [milestoneStatusesList, setMilestoneStatusesList] = useState([]);
  const [headDropDownStatuses, setHeadDropDownStatuses] = useState([]);
  const [milestonesData, setMilestonesData] = useState([]);
  const bulkEditData = useSelector((state) => state.bulkEdit);
  const { classRequestId, classRosterId } = bulkEditData;
  const [allData, setAllData] = useState([]);
  const [order, setOrder] = useState("ASC");
  const initialRender = useRef(true);
  const [totalActiveCount, setTotalActiveCount] = useState({
    total: 0,
    active: 0,
  });

  const sorting = (col) => {
    if (order === "ASC") {
      const sorted = [...milestonesData].sort((a, b) =>
        a[col].toLowerCase() > b[col].toLowerCase() ? 1 : -1,
      );
      setMilestonesData(sorted);
      setOrder("DSC");
      return;
    }

    const sorted = [...milestonesData].sort((a, b) =>
      a[col].toLowerCase() < b[col].toLowerCase() ? 1 : -1,
    );
    setMilestonesData(sorted);
    setOrder("ASC");
  };

  function mergeWithAllData(editedData) {
    const editedMap = new Map(
      editedData.map((record) => [record.ClassTraineeId, record]),
    );

    const masterMap = new Map(
      allData.map((record) => [record.ClassTraineeId, record]),
    );

    const result = [];

    // Pass 1: walk the master — update matches, keep untouched records as-is
    for (const [id, masterRecord] of masterMap) {
      if (editedMap.has(id)) {
        // Spread order: master first, edited second — edited fields win
        result.push({ ...masterRecord, ...editedMap.get(id) });
      } else {
        result.push({ ...masterRecord });
      }
    }

    // Pass 2: append net-new records from edited that weren't in master
    for (const [id, editedRecord] of editedMap) {
      if (!masterMap.has(id)) {
        result.push({ ...editedRecord });
      }
    }

    return result;
  }

  const filtering = useCallback(
    (filter, data) => {
      if (filter === "ALL") {
        setMilestonesData(data);
      } else {
        setMilestonesData(
          data.filter((item) => item.TraineeStatus === props.filterStatus),
        );
      }

      props.closeLoader();
      prepareHeadTextFieldStatuses(milestonesList);
      prepareHeadDateFieldStatuses(milestonesList);
      prepareHeadDropDownStatuses(milestonesList);
      prepareHeadMemoTextFieldStatuses(milestonesList);
    },
    [props, milestonesList],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      let classRosterId = bulkEditData?.traineeDetails[0]?.classRosterId;

      let milestoneResult = [],
        milestoneListResponse = [],
        milestoneStatusReponse = [],
        mappedData = [];

      let traineeData = bulkEditData.traineeDetails.map((item) => {
        return { classTraineeId: item.classTraineeId };
      });

      let traineeDataObject = { classTraineeIdList: traineeData };

      let rowdata = await getOverrideMilestones(traineeDataObject);

      if (rowdata.message === "Success") {
        milestoneResult = await getAllMilestonesWithStatuses(classRosterId);

        milestoneListResponse = milestoneResult?.data?.milestones.filter(
          (item) =>
            item.milestoneType === "OG" || item.milestoneType === "Universal",
        );

        if (milestoneListResponse?.length > 0) {
          milestoneStatusReponse = mergeByMilestoneId(
            milestoneResult?.data?.milestoneStatuses,
            milestoneListResponse,
          ).filter(
            (item) =>
              item.milestoneType === "OG" || item.milestoneType === "Universal",
          );

          setMilestonesList(milestoneListResponse);
          setMilestoneStatusesList(milestoneStatusReponse);

          prepareHeadDropDownStatuses(milestoneListResponse);
          prepareHeadTextFieldStatuses(milestoneListResponse);
          prepareHeadDateFieldStatuses(milestoneListResponse);
          prepareHeadMemoTextFieldStatuses(milestoneListResponse);
        } else {
          setSnack({
            open: true,
            message: `No milestones associated to this roster.`,
            severity: "warning",
          });
        }

        mappedData = createData(rowdata.data, milestoneListResponse);

        setMilestonesData(mappedData);
        dispatch(bulkEditStoreAction.setMilestoneData(mappedData));

        setAllData(mappedData);
        dispatch(bulkEditStoreAction.setMilestoneAllData(mappedData));

        // Calculate total and active counts
        const activeCount = mappedData.filter(
          (item) => item.TraineeStatus === "Active",
        ).length;
        setTotalActiveCount({
          total: mappedData.length,
          active: activeCount,
        });

        if (milestoneListResponse.data) {
          filtering(props.filterStatus, mappedData);
        }
      } else {
        setSnack({
          open: true,
          message: "Failed to load data!",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching milestones data:", error);
      setSnack({
        open: true,
        message: `Some internal error occured: ${error.message}`,
        severity: "error",
      });
    }

    setLoading(false);
  }, [bulkEditData.traineeDetails, props, filtering, dispatch]);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        await fetchData();

        if (!cancelled) {
          applicationUsageLog(
            localStorage.getItem("User"),
            localStorage.getItem("EmailId"),
            "ASTT",
            "Milestones",
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  // Memoize filtered milestones to avoid recalculation
  const filteredMilestones = useMemo(
    () =>
      milestonesList.filter(
        (item) =>
          item.milestoneType === "OG" || item.milestoneType === "Universal",
      ),
    [milestonesList],
  );

  useEffect(() => {
    let cancelled = false;

    const handleReset = async () => {
      if (props.resetStatus === true) {
        try {
          await fetchData();

          if (!cancelled) {
            props.onChange();
          }
        } catch (error) {
          if (!cancelled) {
            console.error("Failed to handle reset:", error);
          }
        }
      }
    };

    handleReset();

    return () => {
      cancelled = true;
    };
  }, [props.resetStatus]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      setLoading(true);

      const timeout = setTimeout(() => {
        filtering(props.filterStatus, allData);
        setLoading(false);
      }, 1000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [props.filterStatus]);

  // Update counts whenever milestonesData changes
  useEffect(() => {
    const activeCount = milestonesData.filter(
      (item) => item.TraineeStatus === "Active",
    ).length;
    setTotalActiveCount({
      total: milestonesData.length,
      active: activeCount,
    });
  }, [milestonesData]);

  const handleChange = useCallback((value, key, row) => {
    setMilestonesData((prev) =>
      prev.map((item) => {
        return {
          ...item,
          ...(item?.ClassTraineeId === row.ClassTraineeId && {
            [key]: value,
          }),
        };
      }),
    );
  }, []);

  // Use useEffect to dispatch after state update
  useEffect(() => {
    dispatch(
      bulkEditStoreAction.setMilestoneData(mergeWithAllData(milestonesData)),
    );
  }, [milestonesData, dispatch]);

  const handleMemoChange = useCallback((value, key, row) => {
    setMilestonesData((prev) =>
      prev.map((item) => {
        return {
          ...item,
          ...(item?.ClassTraineeId === row.ClassTraineeId && {
            [key]: value,
          }),
        };
      }),
    );
  }, []);

  const updateData = (data, key, initialvalue, newValue) => {
    // Don't let "apply to all rows" overwrite a row with a legacy, unparseable Number value.
    const milestoneMeta = milestonesList.find((m) => m.milestone === key);
    const isNumberField = milestoneMeta?.dataType === "Number";

    return data.map((item, i) => {
      const shouldReset =
        (props.filterStatus === "ALL" ||
          item.TraineeStatus === props.filterStatus) &&
        newValue === initialvalue;

      if (shouldReset) {
        return {
          ...item,
          [key]: allData[i][key],
        };
      }

      const existingValue = item[key];
      const isExistingLegacyUnparseable =
        isNumberField &&
        existingValue != null &&
        existingValue !== "" &&
        isNaN(Number(existingValue));
      if (isExistingLegacyUnparseable) {
        return item; // leave this row untouched
      }

      return {
        ...item,
        [key]: newValue,
      };
    });
  };

  const handleAllTextField = (value, key) => {
    let updatedFilterData = updateData(filterData, key, "", value);
    let updatedMilestonesData = updateData(milestonesData, key, "", value);

    setFilterData(updatedFilterData);
    setMilestonesData(updatedMilestonesData);
    dispatch(
      bulkEditStoreAction.setMilestoneData(
        mergeWithAllData(updatedMilestonesData),
      ),
    );

    setGlobalText({ ...globalText, [key]: value });
  };

  const handleAllDateField = (value, key) => {
    if (value === null) {
      setGlobalDate((prev) => ({
        ...prev,
        [key]: null,
      }));
      const updatedMilestonesData = milestonesData.map((row) => ({
        ...row,
        [key]: null,
      }));
      setMilestonesData(updatedMilestonesData);
      dispatch(
        bulkEditStoreAction.setMilestoneData(
          mergeWithAllData(updatedMilestonesData),
        ),
      );
      return;
    }

    const formattedValue = dayjs(value).format("MM-DD-YYYY");

    setGlobalDate((prev) => ({
      ...prev,
      [key]: formattedValue,
    }));

    // Same guard as updateData, for Date fields.
    const updatedMilestonesData = milestonesData.map((row) => {
      const existingValue = row[key];
      const isExistingLegacyUnparseable =
        !!existingValue &&
        !dayjs(existingValue, "MM-DD-YYYY", true).isValid();
      if (isExistingLegacyUnparseable) {
        return row; // leave this row untouched
      }
      return { ...row, [key]: formattedValue };
    });

    setMilestonesData(updatedMilestonesData);
    dispatch(
      bulkEditStoreAction.setMilestoneData(
        mergeWithAllData(updatedMilestonesData),
      ),
    );
  };

  const handleRowDateField = (value, key, row) => {
    if (value === null) {
      const updated = milestonesData.map((item) => ({
        ...item,
        ...(item?.ClassTraineeId === row.ClassTraineeId && {
          [key]: null,
        }),
      }));

      setMilestonesData(updated);
      dispatch(bulkEditStoreAction.setMilestoneData(mergeWithAllData(updated)));
      return;
    }

    const isValid = dayjs(value, "MM/DD/YYYY", true).isValid();
    if (!isValid) {
      return;
    }
    const formattedValue = dayjs(value).format("MM-DD-YYYY");

    const updated = milestonesData.map((item) => ({
      ...item,
      ...(item?.ClassTraineeId === row.ClassTraineeId && {
        [key]: formattedValue,
      }),
    }));

    setMilestonesData(updated);
    dispatch(bulkEditStoreAction.setMilestoneData(mergeWithAllData(updated)));
  };

  const handleAllMemoTextField = (value, key) => {
    let updatedFilterData = updateData(filterData, key, "", value);
    let updatedMilestonesData = updateData(milestonesData, key, "", value);

    setFilterData(updatedFilterData);
    setMilestonesData(updatedMilestonesData);
    dispatch(
      bulkEditStoreAction.setMilestoneData(
        mergeWithAllData(updatedMilestonesData),
      ),
    );

    setGlobalMemoText({ ...globalMemoText, [key]: value });
  };

  const handleAllDropDown = (value, key) => {
    let updatedFilterData = updateData(filterData, key, 0, value);
    let updatedMilestonesData = updateData(milestonesData, key, 0, value);

    setFilterData(updatedFilterData);
    setMilestonesData(updatedMilestonesData);
    dispatch(
      bulkEditStoreAction.setMilestoneData(
        mergeWithAllData(updatedMilestonesData),
      ),
    );

    setHeadDropDownStatuses({ ...headDropDownStatuses, [key]: value });
  };

  const handleMultiSelectAllChange = (event, value, key) => {
    const selectedMilestoneStatusIds = value
      ?.map((item) => item.milestoneStatusId)
      .join(",");

    let updatedFilterData = updateData(
      filterData,
      key,
      0,
      selectedMilestoneStatusIds,
    );
    let updatedMilestonesData = updateData(
      milestonesData,
      key,
      0,
      selectedMilestoneStatusIds,
    );

    setFilterData(updatedFilterData);
    setMilestonesData(updatedMilestonesData);
    dispatch(
      bulkEditStoreAction.setMilestoneData(
        mergeWithAllData(updatedMilestonesData),
      ),
    );

    setHeadDropDownStatuses({
      ...headDropDownStatuses,
      [key]: selectedMilestoneStatusIds,
    });
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleTextInput = (event, row, i) => {
    let milestones = milestonesData.map((item) => {
      if (
        item.ClassTraineeId === row.ClassTraineeId &&
        item.TraineeStatus === row.TraineeStatus
      ) {
        return {
          ...item,
          [event.target.name]:
            event.target.value === "" ? null : event.target.value,
        };
      }
      return item;
    });

    setMilestonesData(milestones);

    dispatch(
      bulkEditStoreAction.setMilestoneData(mergeWithAllData(milestones)),
    );
  };

  const shouldIncludeRecord = (item, data) => {
    
    const dataTypeLower = data?.dataType?.toLowerCase();
    const value = item[data.milestone];

    // Check if value exists and is not empty
    let hasValue = false;

    if (dataTypeLower === "multiselect" || dataTypeLower === "list") {
      // For dropdowns, check if value is not 0 or "0"
      hasValue = value && value !== 0 && value !== "0" && value !== "";
    } else if (
      dataTypeLower === "date" ||
      dataTypeLower === "memo" ||
      dataTypeLower === "text" ||
      dataTypeLower === "number"
    ) {
      // For text-based fields, check if not null/undefined/empty
      hasValue = value !== null && value !== undefined && value !== "";
    } else {
      // Default check for other types
      hasValue = value && value !== 0 && value !== "0";
    }

    const isSpecialType = [
      "number",
      "text",
      "multiselect",
      "memo",
      "date",
    ].includes(dataTypeLower);

    return hasValue || isSpecialType;
  };

  const getMilestoneStatusId = (item, data) => {
    const dataTypeLower = data?.dataType?.toLowerCase();
    const value = item[data.milestone];

    // For List or MultiSelect, return the value as string if it exists
    if (dataTypeLower === "list" || dataTypeLower === "multiselect") {
      // Check if value exists and is not "0" or 0
      if (value && value !== 0 && value !== "0" && value !== "") {
        return value.toString();
      }
      return "0";
    }

    // For other types, return "0"
    return "0";
  };

  const prepareClassTraineeMilestoneRecord = (data) => {
    let records = [];

    milestonesData.forEach((item) => {
      const value = item[data.milestone];
      const dataTypeLower = data?.dataType?.toLowerCase();

      // Check if there's a meaningful value
      const hasValue =
        dataTypeLower === "multiselect" || dataTypeLower === "list"
          ? value && value !== 0 && value !== "0" && value !== ""
          : shouldIncludeRecord(item, data);
      if (hasValue) {
        records.push({
          classTraineeId: item.ClassTraineeId,
          mileStoneId: data.milestoneID,
          milestoneStatusId: getMilestoneStatusId(item, data),
          status: value?.toString() || "",
          agentComments: item.AgentComments,
          modifiedBy: localStorage.getItem("User"),
        });
      } else {
        records.push({
          classTraineeId: item.ClassTraineeId,
          mileStoneId: data?.milestoneID || 0,
          milestoneStatusId: "0",
          status: "",
          agentComments: item.AgentComments,
          modifiedBy: localStorage.getItem("User"),
        });
      }
    });

    return records;
  };

  const prepareSavePayload = () => {
    let payload = [];
    if (milestonesList.length > 0) {
      payload = milestonesList.map((item) => {
        return prepareClassTraineeMilestoneRecord(item);
      });
      payload = payload.flat(100);
    } else {
      milestonesData.forEach((item) => {
        if (JSON.stringify(allData) !== JSON.stringify(milestonesData)) {
          payload.push({
            classTraineeId: item.ClassTraineeId,
            mileStoneId: 0,
            milestoneStatusId: 0,
            agentComments: item.AgentComments,
            modifiedBy: localStorage.getItem("User"),
          });
        }
      });
    }

    return payload;
  };

  const navigateBack = () => {
    navigate(
      `/home/searchrosters?classRequestId=${classRequestId}&classRosterId=${classRosterId}`,
    );
  };

  const saveCloseHandler = async (e) => {
    setLoading(true);

    const savePayload = prepareSavePayload();

    if (savePayload.length > 0) {
      const response = await savemilestonesData(savePayload);
      if (response.message === "Success") {
        const obj = {
          open: true,
          message: "Saved Succesfully !!",
          severity: "success",
        };
        dispatch(SnackBarStoreAction.updateSnack(obj));

        prepareHeadDropDownStatuses(milestonesList);
        prepareHeadTextFieldStatuses(milestonesList);
        prepareHeadMemoTextFieldStatuses(milestonesList);
        prepareHeadDateFieldStatuses(milestonesList);
        fetchData();
      } else {
        const obj = {
          open: true,
          message: "Failed to Save",
          severity: "error",
        };
        dispatch(SnackBarStoreAction.updateSnack(obj));
      }

      dispatch(bulkEditStoreAction.setPreviousTabName(""));

      navigateBack();
    } else {
      setSnack({
        open: true,
        message: "No changes to save.",
        severity: "warning",
      });
    }
    setLoading(false);
  };

  const saveHandler = async (e) => {
    setLoading(true);

    const savePayload = prepareSavePayload();
    if (savePayload.length > 0) {
      const response = await savemilestonesData(savePayload);

      if (response.message === "Success") {
        setSnack({
          open: true,
          message: "Saved Succesfully",
          severity: "success",
        });
        setGlobalText("");
        setGlobalMemoText("");
        prepareHeadDropDownStatuses(milestonesList);
        prepareHeadTextFieldStatuses(milestonesList);
        prepareHeadMemoTextFieldStatuses(milestonesList);
        prepareHeadDateFieldStatuses(milestonesList);
        fetchData();
      } else {
        setSnack({
          open: true,
          message: "Failed to Save",
          severity: "error",
        });
      }
    } else {
      setSnack({
        open: true,
        message: "No changes to save.",
        severity: "warning",
      });
    }
    setLoading(false);
  };

  const prepareHeadTextFieldStatuses = (data) => {
    let globalText = data.map((item) => {
      return {
        [item?.milestone]: "",
      };
    });

    setGlobalText(Object.assign({}, ...globalText));
  };

  const prepareHeadDateFieldStatuses = (data) => {
    let globalDateInit = {};
    data.forEach((item) => {
      if (item?.dataType?.toLowerCase() === "date") {
        globalDateInit[item?.milestone] = "";
      }
    });
    setGlobalDate(globalDateInit);
  };

  const prepareHeadMemoTextFieldStatuses = (data) => {
    let globalMemoText = data.map((item) => {
      return {
        [item?.milestone]: "",
      };
    });

    setGlobalMemoText(Object.assign({}, ...globalMemoText));
  };

  const prepareHeadDropDownStatuses = (data) => {
    let statuses = data.map((item) => {
      if (item?.dataType?.toLowerCase() === "multiselect") {
        return {
          [item?.milestone]: "",
        };
      }
      return {
        [item?.milestone]: 0,
      };
    });

    setHeadDropDownStatuses(Object.assign({}, ...statuses));
  };

  // Memoize getStatuses to avoid filtering on every render
  const getStatuses = useCallback(
    (mileStoneId) => {
      return milestoneStatusesList.filter(
        (item) => item?.milestoneID === mileStoneId,
      );
    },
    [milestoneStatusesList],
  );

  const renderTextCell = (oracleId, name, traineeStatus, isInactive) => (
    <Grid item xs={0.6} align="center">
      <Typography
        sx={{
          fontFamily: "Urbanist",
          fontSize: "13px",
          color: "#3F5765",
          textDecoration: isInactive ? "line-through" : "none",
        }}
      >
        {`${name} (${oracleId})`}
      </Typography>
      {isInactive && (
        <Typography
          className={classes.blueColor}
          sx={{ fontSize: "12px", color: "#3299FF", fontWeight: "bold" }}
        >
          {traineeStatus}
        </Typography>
      )}
    </Grid>
  );

  const handleMultiSelectChange = useCallback((event, value, key, row) => {
    const selectedMilestoneStatusIds =
      value?.length === 0
        ? "0"
        : value?.map((item) => item.milestoneStatusId).join(",");

    setMilestonesData((prev) =>
      prev.map((item) => {
        return {
          ...item,
          ...(item?.ClassTraineeId === row.ClassTraineeId && {
            [key]: selectedMilestoneStatusIds,
          }),
        };
      }),
    );
  }, []);

  const getMultiSelectSelectedValue = useCallback(
    (milestoneStatusIds, milestoneId) => {
      const milestoneStatuses = getStatuses(milestoneId);
      return milestoneStatuses.filter((status) =>
        milestoneStatusIds?.includes(status.milestoneStatusId.toString()),
      );
    },
    [getStatuses],
  );

  return (
    <>
      {loading ? <BackdropComponent /> : null}

      <TableContainer
        className={classes.container}
        component={Paper}
        sx={{ overflowX: "auto", overflowY: "auto" }}
      >
        <Table stickyHeader aria-label="sticky table" sx={{ minWidth: 1600 }}>
          <TableHead>
            <TableRow>
              <NameHeaderCell
                sorting={sorting}
                t={t}
                order={order}
                totalActiveCount={totalActiveCount}
                classes={classes}
              />

              {filteredMilestones.map((item) => (
                <MilestoneHeaderCell
                  key={item?.milestoneID}
                  item={item}
                  headDropDownStatuses={headDropDownStatuses}
                  globalMemoText={globalMemoText}
                  globalDate={globalDate}
                  globalText={globalText}
                  getStatuses={getStatuses}
                  handleAllDropDown={handleAllDropDown}
                  handleAllMemoTextField={handleAllMemoTextField}
                  handleMultiSelectAllChange={handleMultiSelectAllChange}
                  handleAllDateField={handleAllDateField}
                  handleAllTextField={handleAllTextField}
                  setGlobalMemoText={setGlobalMemoText}
                  setGlobalText={setGlobalText}
                  getMultiSelectSelectedValue={getMultiSelectSelectedValue}
                  classes={classes}
                />
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {milestonesData.length > 0 &&
              milestonesData.map((row, index) => (
                <TableRow
                  key={`${row?.ClassTraineeId}-${index}`}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor: "#fff",
                  }}
                  align="center"
                >
                  <TraineeInfoCell
                    row={row}
                    renderTextCell={renderTextCell}
                    handleTextInput={handleTextInput}
                    classes={classes}
                  />

                  {filteredMilestones.map((item) => (
                    <MilestoneDataCell
                      key={`${row?.OracleId}-${item?.milestoneID}`}
                      row={row}
                      item={item}
                      getStatuses={getStatuses}
                      handleChange={handleChange}
                      handleRowDateField={handleRowDateField}
                      handleMemoChange={handleMemoChange}
                      handleMultiSelectChange={handleMultiSelectChange}
                      getMultiSelectSelectedValue={getMultiSelectSelectedValue}
                    />
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Buttons */}
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          paddingTop: 1,
          gap: 1,
        }}
      >
        <CustomOutlinedButton variant="outlined" onClick={navigateBack}>
          {t("CANCEL")}
        </CustomOutlinedButton>
        <CustomButton onClick={(e) => saveHandler(e)}>{t("SAVE")}</CustomButton>
        <CustomButton onClick={(e) => saveCloseHandler(e)}>
          {t("SAVE+CLOSE")}
        </CustomButton>
      </Grid>

      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </>
  );
};

MilestonesV2.propTypes = {
  closeLoader: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  filterStatus: PropTypes.string.isRequired,
  resetStatus: PropTypes.bool.isRequired,
};

export default MilestonesV2;
